import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { isBrowser } from '../platform.util';

export interface ChunkedTokenConfig {
  projectName: string;
  portalType: string;
  maxChunkSize: number;
  cookieExpiryDays: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChunkedTokenStorage {
  private platformId = inject(PLATFORM_ID);
  private cookieService = inject(CookieService);

  private readonly config: ChunkedTokenConfig = {
    projectName: 'apa',
    portalType: 'client',
    maxChunkSize: 4000, // Cookie size limit is ~4KB
    cookieExpiryDays: 7
  };

  /**
   * Store token in chunks as cookies
   */
  storeToken(token: string, itemName: string): boolean {
    if (!isBrowser(this.platformId) || !token) {
      return false;
    }

    try {
      // Clear existing chunks first
      this.clearToken(itemName);

      // Split token into chunks
      const chunks = this.splitIntoChunks(token);
      
      // Store each chunk as a separate cookie
      chunks.forEach((chunk, index) => {
        const cookieName = this.getCookieName(itemName, index);
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + this.config.cookieExpiryDays);
        
        this.cookieService.set(cookieName, chunk, expiryDate, '/', undefined, false, 'Lax');
      });

      // Store chunk count as metadata
      const countCookieName = this.getCookieName(itemName, 'count');
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + this.config.cookieExpiryDays);
      
      this.cookieService.set(countCookieName, chunks.length.toString(), expiryDate, '/', undefined, false, 'Lax');
      
      return true;
    } catch (error) {
       return false;
    }
  }

  /**
   * Retrieve token from chunks
   */
  getToken(itemName: string): string | null {
    if (!isBrowser(this.platformId)) {
      return null;
    }

    try {
      // Get chunk count
      const countCookieName = this.getCookieName(itemName, 'count');
      const chunkCount = this.cookieService.get(countCookieName);
      
      if (!chunkCount) {
        return null;
      }

      const count = parseInt(chunkCount, 10);
      if (isNaN(count) || count <= 0) {
        return null;
      }

      // Retrieve all chunks
      const chunks: string[] = [];
      for (let i = 0; i < count; i++) {
        const cookieName = this.getCookieName(itemName, i);
        const chunk = this.cookieService.get(cookieName);
        
        if (!chunk) {
          // If any chunk is missing, return null
          return null;
        }
        
        chunks.push(chunk);
      }

      // Reconstruct the token
      return chunks.join('');
    } catch (error) {
       return null;
    }
  }

  /**
   * Clear token chunks
   */
  clearToken(itemName: string): void {
    if (!isBrowser(this.platformId)) {
      return;
    }

    try {
      // Get chunk count first
      const countCookieName = this.getCookieName(itemName, 'count');
      const chunkCount = this.cookieService.get(countCookieName);
      
      if (chunkCount) {
        const count = parseInt(chunkCount, 10);
        if (!isNaN(count) && count > 0) {
          // Clear all chunk cookies
          for (let i = 0; i < count; i++) {
            const cookieName = this.getCookieName(itemName, i);
            this.cookieService.delete(cookieName, '/');
          }
        }
      }

      // Clear count cookie
      this.cookieService.delete(countCookieName, '/');
    } catch (error) {
      }
  }

  /**
   * Check if token exists
   */
  hasToken(itemName: string): boolean {
    if (!isBrowser(this.platformId)) {
      return false;
    }

    const countCookieName = this.getCookieName(itemName, 'count');
    return this.cookieService.check(countCookieName);
  }

  /**
   * Split token into chunks
   */
  private splitIntoChunks(token: string): string[] {
    const chunks: string[] = [];
    const maxSize = this.config.maxChunkSize;
    
    for (let i = 0; i < token.length; i += maxSize) {
      chunks.push(token.slice(i, i + maxSize));
    }
    
    return chunks;
  }

  /**
   * Generate cookie name based on project, portal type, item name, and index
   */
  private getCookieName(itemName: string, index: string | number): string {
    return `${this.config.projectName}-${this.config.portalType}-${itemName}-${index}`;
  }
}
